import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '../../utils/api'
import { generatePayrollJournal, exportJournalCSV, type CSVFormat, type DetailLevel, type JournalEntry } from './payroll-journal'
import { centsToEur } from '../paie/payroll-engine'
import type { PayrollResult } from '../paie/payroll-engine'
import styles from './AccountingPage.module.css'

export function AccountingPage() {
  const { t } = useTranslation()
  const [detailLevel, setDetailLevel] = useState<DetailLevel>('global')
  const [exportFormat, setExportFormat] = useState<CSVFormat>('sage_bob')
  const [year, setYear] = useState(new Date().getFullYear())
  const [month, setMonth] = useState(new Date().getMonth() + 1)
  const [entries, setEntries] = useState<JournalEntry[]>([])
  const [generating, setGenerating] = useState(false)

  const handleGenerateJournal = async () => {
    setGenerating(true)
    const res = await apiFetch<{ payslips: Array<Record<string, unknown>> }>(`/payslips?year=${year}&month=${month}`)
    if (!res.data || res.data.payslips.length === 0) {
      alert(t('accounting.no_journal'))
      setGenerating(false)
      return
    }
    const payrollResults = res.data.payslips.map(p => ({
      employeeId: (p['employee_id'] as string) ?? '',
      employeeName: (p['employee_name'] as string) ?? '',
      department: '',
      result: JSON.parse((p['details_json'] as string) ?? '{}') as PayrollResult,
    }))
    setEntries(generatePayrollJournal({ periodYear: year, periodMonth: month, payrollResults, detailLevel }))
    setGenerating(false)
  }

  const handleExportCSV = () => {
    if (entries.length === 0) return
    const csv = exportJournalCSV(entries, exportFormat)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `journal_paie_${year}_${String(month).padStart(2, '0')}_${exportFormat}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>{t('accounting.title')}</h1>
        <p className={styles.subtitle}>{t('accounting.subtitle')}</p>
      </div>

      <div className={styles.config}>
        <div className={styles.configField}>
          <label>{t('run_payroll.month')}</label>
          <select className={styles.select} value={month} onChange={e => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
          </select>
        </div>
        <div className={styles.configField}>
          <label>{t('run_payroll.year')}</label>
          <select className={styles.select} value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className={styles.configField}>
          <label>{t('accounting.detail_level')}</label>
          <select className={styles.select} value={detailLevel} onChange={e => setDetailLevel(e.target.value as DetailLevel)}>
            <option value="global">{t('accounting.level_global')}</option>
            <option value="salarie">{t('accounting.level_employee')}</option>
            <option value="centre_cout">{t('accounting.level_cost_center')}</option>
          </select>
        </div>
        <div className={styles.configField}>
          <label>{t('accounting.export_format')}</label>
          <select className={styles.select} value={exportFormat} onChange={e => setExportFormat(e.target.value as CSVFormat)}>
            <option value="sage_bob">Sage BOB</option>
            <option value="exact_online">Exact Online</option>
            <option value="generic">{t('accounting.format_generic')}</option>
          </select>
        </div>
      </div>

      <div className={styles.actions}>
        <button className={styles.primaryBtn} onClick={handleGenerateJournal} disabled={generating}>
          {generating ? t('common.loading') : t('accounting.generate_journal')}
        </button>
        {entries.length > 0 && (
          <button className={styles.secondaryBtn} onClick={handleExportCSV}>
            {t('accounting.export_csv')} ({exportFormat})
          </button>
        )}
      </div>

      {entries.length > 0 && (
        <div className={styles.section}>
          <h2>{t('accounting.mapping_title')} — {entries.length} ecritures</h2>
          <div className={styles.mappingTable}>
            <div className={styles.mappingHeader}>
              <span>{t('accounting.col_item')}</span>
              <span>{t('accounting.col_debit')}</span>
              <span>{t('accounting.col_credit')}</span>
            </div>
            {entries.map((e, i) => (
              <div key={i} className={styles.mappingRow}>
                <span>{e.accountCode} — {e.label}</span>
                <span>{e.debitCents > 0 ? centsToEur(e.debitCents) : ''}</span>
                <span>{e.creditCents > 0 ? centsToEur(e.creditCents) : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {entries.length === 0 && !generating && (
        <div className={styles.empty}><p>{t('accounting.no_journal')}</p></div>
      )}
    </div>
  )
}
